/**
 * Country-Based Stock Availability
 * ─────────────────────────────────
 * Loaded once, site-wide, from layout/theme.liquid (not deferred, so it
 * runs as early as possible — right after body content is parsed, before
 * any click can slip through).
 *
 * Scans the page for every .uk-stock-check marker (rendered by
 * snippets/stock-check.liquid inside a <product-form>: the product page's
 * Add to Cart button via buy-buttons.liquid, and each product card's
 * quick-add "Add to Bag" button via card-product.liquid). Each marker is
 * checked independently by SKU against our fulfilment app for the
 * visitor's current storefront country, and its own button is disabled if
 * that country's fulfilment location (e.g. N PACK for international vs UK
 * Warehouse for GB) is out of stock.
 *
 * While a check is in flight, that button shows the theme's own loading
 * spinner (the same one product-form.js uses during a real Add to Cart
 * submit) so customers can't click through before we know the answer.
 *
 * New product-forms that appear after page load (a quick-add "Choose
 * options" modal fetching a product page, a facet/sort/pagination grid
 * refresh) are picked up by a MutationObserver and checked the same way.
 */

(function () {
  'use strict';

  // ── Configuration ──────────────────────────────────────────────────────────
  // APP_URL is set by layout/theme.liquid to avoid hardcoding it here
  const APP_URL = window.UK_FULFILLMENT_APP_URL || 'https://welton-london.lincraft-app.com';
  const COUNTRY = window.COUNTRY_ISO || 'GB';

  // CSS class added to a button when it's forced into "sold out" state
  const SOLD_OUT_CLASS = 'uk-sold-out';

  // Selectors for a product-form's Add to Cart / quick-add button.
  // Covers Dawn theme and most common third-party themes.
  const ADD_TO_CART_SELECTORS = [
    '[name="add"]',                          // Standard Shopify
    '.product-form__submit',                 // Dawn theme (product page)
    '.quick-add__submit',                    // Dawn theme (product card)
    '.btn--add-to-cart',                     // Common third-party
    '[data-add-to-cart]',                    // Custom themes
    '#AddToCartForm button[type="submit"]',  // Older themes
    '.product-form__cart-submit',            // Your current theme button
    '.btn.product-form__cart-submit',        // More specific fallback
  ];

  function findAddToCartButton(scope) {
    for (const selector of ADD_TO_CART_SELECTORS) {
      const btn = scope.querySelector(selector);
      if (btn) return btn;
    }
    return null;
  }

  /**
   * The element that actually holds the button's visible label.
   * Dawn (and most themes with an icon/spinner inside the button) wrap the
   * label in its own <span> so the spinner can overlay it — writing to
   * button.textContent directly would wipe out that span and the spinner
   * along with it. Falls back to the button itself for simpler markup.
   */
  function getButtonTextElement(button) {
    return button.querySelector('span') || button;
  }

  // ── Loading State ─────────────────────────────────────────────────────────
  //
  // Reuses Dawn's own `.button.loading` + `.loading__spinner` pattern (the
  // same one product-form.js toggles during a real Add to Cart submit), so
  // the "checking availability" state looks identical to the theme's own
  // loading state instead of introducing a new one.

  function showLoading(button) {
    if (button.dataset.ukCheckingStock === 'true') return;
    button.dataset.ukCheckingStock = 'true';

    // Remember whether Shopify itself had already disabled this button
    // (e.g. the variant is genuinely sold out everywhere) so we don't
    // accidentally re-enable it later just because the country check
    // came back "available".
    if (button.dataset.ukServerDisabled === undefined) {
      button.dataset.ukServerDisabled = button.disabled ? 'true' : 'false';
    }

    button.disabled = true;
    button.classList.add('loading');

    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');
  }

  function hideLoading(button) {
    button.dataset.ukCheckingStock = 'false';
    button.classList.remove('loading');

    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.add('hidden');
  }

  // ── Stock Check ───────────────────────────────────────────────────────────

  /**
   * Set the button to "Sold Out" (disabled, different text).
   * Stores the original text so we can restore it if the variant changes.
   */
  function markSoldOut(button) {
    const textEl = getButtonTextElement(button);
    if (button.dataset.originalText === undefined) {
      button.dataset.originalText = textEl.textContent.trim();
    }
    button.disabled = true;
    textEl.textContent = 'Sold Out';
    button.classList.add(SOLD_OUT_CLASS);

    // Hide dynamic checkout / Shop Pay button, if this form has one
    const paymentButton = button.closest('product-form, .quick-add, .product-form')
      ?.querySelector('[data-shopify="payment-button"]');
    if (paymentButton) paymentButton.style.display = 'none';
  }

  /**
   * Restore the button to its original enabled state — unless Shopify had
   * already disabled it server-side (genuinely sold out everywhere), in
   * which case we leave it alone.
   */
  function markAvailable(button) {
    if (button.dataset.ukServerDisabled === 'true') return;

    button.disabled = false;
    if (button.dataset.originalText) {
      getButtonTextElement(button).textContent = button.dataset.originalText;
    }
    button.classList.remove(SOLD_OUT_CLASS);

    const paymentButton = button.closest('product-form, .quick-add, .product-form')
      ?.querySelector('[data-shopify="payment-button"]');
    if (paymentButton) paymentButton.style.display = '';
  }

  /**
   * Fetch stock for a given SKU/country from our fulfilment app.
   * Returns { available: boolean, quantity: number } or null on error.
   */
  async function fetchStock(sku) {
    if (!sku) return null;

    try {
      const response = await fetch(`${APP_URL}/api/stock/${encodeURIComponent(sku)}/${COUNTRY}`, {
        method: 'GET'
      });

      if (!response.ok) return null;
      return await response.json();
    } catch {
      // Network error or timeout — fail silently, don't block the button
      return null;
    }
  }

  /**
   * Check stock for one product-form instance (identified by its
   * .uk-stock-check marker) and update that instance's own button.
   */
  async function checkForm(form) {
    const wrapper = form.querySelector('.uk-stock-check');
    if (!wrapper) return;

    const button = findAddToCartButton(form);
    if (!button) return;

    const sku = wrapper.dataset.sku;
    if (!sku) return;

    showLoading(button);

    const stockData = await fetchStock(sku);

    hideLoading(button);

    if (stockData && stockData.available <= 0) {
      markSoldOut(button);
    } else {
      // Available, or API failed (fail open — don't block checkout)
      markAvailable(button);
    }
  }

  /**
   * Scan the page for product-forms carrying a stock-check marker and
   * check each one that hasn't already been checked. Safe to call
   * repeatedly (e.g. from a MutationObserver) — already-checked forms are
   * skipped, so this only ever does work for genuinely new markup.
   */
  function scanAndCheckAll() {
    document.querySelectorAll('product-form').forEach((form) => {
      if (form.dataset.ukStockChecked === 'true') return;
      if (!form.querySelector('.uk-stock-check')) return;
      form.dataset.ukStockChecked = 'true';
      checkForm(form);
    });
  }

  // ── Initialization ─────────────────────────────────────────────────────────

  scanAndCheckAll();

  // Pick up product-forms added after page load: a quick-add "Choose
  // options" modal fetching a product page, or a facet/sort/pagination
  // grid refresh (both replace markup without a full page reload).
  const gridObserver = new MutationObserver((mutations) => {
    const hasAddedNodes = mutations.some((m) => m.addedNodes && m.addedNodes.length > 0);
    if (hasAddedNodes) scanAndCheckAll();
  });
  gridObserver.observe(document.body, { childList: true, subtree: true });

  // ── Variant Change Listener (product page only — cards are single-variant,
  // so there's nothing to switch there) ───────────────────────────────────
  //
  // Dawn (this theme's base) announces variant changes through its own
  // pubsub helper rather than a plain DOM CustomEvent, so we hook into
  // that directly when it's present.

  function handleVariantChanged(sectionId, variant) {
    if (!variant || !variant.sku) return;

    const form = sectionId
      ? document.querySelector(`product-form[data-section-id="${sectionId}"]`)
      : null;
    if (!form) return;

    const wrapper = form.querySelector('.uk-stock-check');
    if (!wrapper) return;

    wrapper.dataset.sku = variant.sku;
    wrapper.dataset.variantId = variant.id;

    // A new variant may flip Shopify's own server-rendered disabled state
    // (e.g. switching from a sold-out size to an in-stock one), so re-read
    // it rather than trusting the value captured for the previous variant.
    const button = findAddToCartButton(form);
    if (button) delete button.dataset.ukServerDisabled;

    checkForm(form);
  }

  function attachVariantChangeListener() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      // Dawn's pubsub system (loaded via pubsub.js/constants.js).
      // Payload shape: { data: { sectionId, html, variant } }
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        handleVariantChanged(event?.data?.sectionId, event?.data?.variant);
      });
    } else {
      // Fallback for other themes that dispatch a plain CustomEvent instead
      // (no section scoping available, so just re-check everything).
      document.addEventListener('variant:change', (event) => {
        const variant = event.detail?.variant;
        if (!variant || !variant.sku) return;
        document.querySelectorAll('.uk-stock-check').forEach((wrapper) => {
          wrapper.dataset.sku = variant.sku;
          wrapper.dataset.variantId = variant.id;
        });
        document.querySelectorAll('product-form').forEach((form) => {
          delete form.dataset.ukStockChecked;
          const button = findAddToCartButton(form);
          if (button) delete button.dataset.ukServerDisabled;
        });
        scanAndCheckAll();
      });
    }
  }

  // This script is intentionally NOT deferred, so the initial scan above
  // starts (and disables buttons) as early as possible -- before
  // pubsub.js/constants.js (which are deferred) have necessarily run.
  // Registering the variant-change listener isn't time-critical the same
  // way, so wait for those to be ready rather than deferring the whole file.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    attachVariantChangeListener();
  } else {
    document.addEventListener('DOMContentLoaded', attachVariantChangeListener);
  }
})();
